/**
 * Adapter HOC: Converts React Router params/search to Next.js-style props
 *
 * This allows us to keep the old component structure while migrating to React Router.
 * Instead of manually converting each page, wrap them with this HOC.
 *
 * Usage:
 *   const MyPage = ({ slug, query, projectId }) => { ... }
 *   export default withRouterParams(MyPage);
 */

import React from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

const withRouterParams = (Component) => {
  const WithRouterParamsComponent = (props) => {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    // Convert URL params ending in 'Id' to numbers (to match API response types)
    const convertedParams = {};
    for (const [key, value] of Object.entries(params)) {
      if (key.endsWith('Id') && value && /^\d+$/.test(value)) {
        convertedParams[key] = parseInt(value, 10);
      } else {
        convertedParams[key] = value;
      }
    }

    // Convert searchParams to plain object (like Next.js query)
    // Handle both array format (?key=1&key=2) and slash-delimited (?key=1/2)
    const query = {};
    const processedKeys = new Set();

    for (const [key] of searchParams.entries()) {
      if (processedKeys.has(key)) continue;
      processedKeys.add(key);

      const allValues = searchParams.getAll(key);

      if (allValues.length === 1) {
        // Single value - convert to number if it's an ID param
        const value = allValues[0];
        query[key] =
          key.endsWith('Id') && /^\d+$/.test(value)
            ? parseInt(value, 10)
            : value;
      } else {
        // Multiple values - join with '/' to match production format
        query[key] = allValues.join('/');
      }
    }

    // Merge URL params with search params (Next.js behavior)
    // IMPORTANT: Also merge props from parent (e.g., createInitiativeWrapper)
    const mergedQuery = { ...convertedParams, ...query, ...props };

    // Build props object similar to Next.js getInitialProps
    let routerProps = {
      // URL params (e.g., :slug, :projectId) - with IDs converted to numbers
      ...convertedParams,

      // Props from parent (e.g., initiativeType from createInitiativeWrapper)
      ...props,

      // Query string as object
      query: mergedQuery,

      // Location object (in case component needs pathname, etc.)
      location,
    };

    // Execute getInitialProps if the component has one (Next.js compatibility)
    if (Component.getInitialProps) {
      const initialProps = Component.getInitialProps({
        query: mergedQuery,
        params: convertedParams,
        location,
      });
      routerProps = { ...routerProps, ...initialProps };
    }

    return <Component {...routerProps} />;
  };

  WithRouterParamsComponent.displayName = `WithRouterParams(${Component.displayName || Component.name || 'Component'})`;

  return WithRouterParamsComponent;
};

export default withRouterParams;
